import React, { useRef, useState } from 'react';

import { PaperProps } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ResourceSection } from 'components/SmartAssistantSections/ResourceSection';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { MAX_FILE_COUNT } from 'shared/constants/general-constants';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { getAllowedResourceTypes } from 'shared/constants/resource';
import { Resource } from 'shared/interfaces/Resource';
import { SmartAssistant, SmartAssistantType } from 'shared/interfaces/SmartAssistant';

interface ResourcesManagementSectionProps extends PaperProps {
  smartAssistant: SmartAssistant;
  onSaveClbck?: () => void;
}

export function ResourcesManagementSection({
  smartAssistant,
  onSaveClbck,
  ...paperProps
}: ResourcesManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();
  const hasChangesRef = useRef(false);

  const [showMinResourcesError, setShowMinResourcesError] = useState(false);
  const [smartAssistantResourceIds, setSmartAssistantResourceIds] = useState<Set<string>>(
    new Set(smartAssistant.resources)
  );

  const maxResourceCount = subscription.product.resourcePerSmartAssistantLimit ?? MAX_FILE_COUNT;
  const zeroResourcesAllowed =
    smartAssistant.type === SmartAssistantType.PHONE || smartAssistant.type === SmartAssistantType.SHOPIFY;

  const saveMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      hasChangesRef.current = false;
      onSaveClbck?.();
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_RESOURCES');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_RESOURCES')
  });

  const onResourceSelect = (resource: Resource) => {
    if (smartAssistantResourceIds.has(resource._id)) return;
    const newSelectedResourceIds = new Set(smartAssistantResourceIds);
    newSelectedResourceIds.add(resource._id);
    setSmartAssistantResourceIds(newSelectedResourceIds);
    setShowMinResourcesError(false);
  };

  const onResourceUnselect = (resource: Resource) => {
    const lastResource = smartAssistantResourceIds.size === 1;
    if (!smartAssistantResourceIds.has(resource._id) || (lastResource && !zeroResourcesAllowed)) return;
    const newSelectedResourceIds = new Set(smartAssistantResourceIds);
    newSelectedResourceIds.delete(resource._id);
    setSmartAssistantResourceIds(newSelectedResourceIds);
  };

  const onResourceClick = (resource: Resource) => {
    hasChangesRef.current = true;
    if (smartAssistantResourceIds.has(resource._id)) onResourceUnselect(resource);
    else onResourceSelect(resource);
  };

  const onSave = () => {
    if (smartAssistantResourceIds.size <= 0) setShowMinResourcesError(true);
    saveMutation.mutate({
      id: smartAssistant._id,
      resourceIds: Array.from(smartAssistantResourceIds.values())
    });
  };

  const onCancel = () => {
    setSmartAssistantResourceIds(new Set(smartAssistant.resources));
    hasChangesRef.current = false;
  };

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.RESOURCES_SECTION.TITLE_SHORT')}
      hasChanges={hasChangesRef.current}
      disabled={
        (smartAssistantResourceIds.size === 0 && !zeroResourcesAllowed) ||
        smartAssistantResourceIds.size > maxResourceCount
      }
      loading={saveMutation.isLoading}
      onSave={onSave}
      onCancel={onCancel}
      withMarginBottom>
      <ResourceSection
        h="100%"
        onResourceClick={(resource) => onResourceClick(resource as Resource)}
        selectedResources={smartAssistantResourceIds}
        showMinResourcesError={showMinResourcesError}
        sortOnFirstLoad={true}
        allowedResourceTypes={getAllowedResourceTypes(smartAssistant.type)}
        {...paperProps}
      />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
